import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Video, Hash, ArrowLeft, Copy, Check, Users, Lock,
  Plus, Crown, ShieldAlert, Sparkles, X, ExternalLink, Square,
  Radio, Mail, MessageCircle, Link as LinkIcon,
  Calendar as CalIcon, MapPin, Clock, Trash2, Volume2, VolumeX, Mic, MicOff,
} from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useActiveMeeting } from "../../context/ActiveMeetingContext";
import { toast } from "sonner";
import BroadcastFrame, { THEMES, detectTheme } from "../../components/BroadcastFrame";
import MeetingNotesInline from "../../components/MeetingNotesInline";
import VirtualBackgroundPicker from "../../components/VirtualBackgroundPicker";

/* eslint-disable react-hooks/exhaustive-deps */

// ──────────────────────────────────────────────────────────────────────────────
// SHARE HELPERS — used by the Create modal AND the My-Meetings list rows.
// ──────────────────────────────────────────────────────────────────────────────
function buildJoinLink(code) {
  if (typeof window === "undefined") return "";
  // Public guest URL — works for both members and non-members.
  return `${window.location.origin}/join?code=${encodeURIComponent(code)}`;
}

function buildShareText(meeting) {
  const link = buildJoinLink(meeting.code);
  const lines = [
    `You're invited to an Ambedkarite Buddhist Global Network UK meeting:`,
    "",
    `📿  ${meeting.title}`,
  ];
  if (meeting.description) lines.push("", meeting.description);
  if (meeting.start_at) {
    try {
      const dt = new Date(meeting.start_at);
      const fmt = dt.toLocaleString("en-GB", {
        weekday: "long", day: "numeric", month: "long", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
      const dur = meeting.duration_min ? ` · ${meeting.duration_min} min` : "";
      lines.push("", `🕒  ${fmt}${dur}`);
    } catch { /* ignore */ }
  }
  if (meeting.location) {
    lines.push(`📍  ${meeting.location}`);
  } else if (meeting.mode === "online" || !meeting.mode) {
    lines.push(`💻  Online meeting`);
  }
  lines.push(
    "",
    `🔑  Code: ${meeting.code}`,
    `🔗  Join link: ${link}`,
    "",
    `(No account needed, just click the link, enter your name, and join.)`,
    "",
    "— Ambedkarite Buddhist Global Network UK"
  );
  return lines.join("\n");
}

function ShareMeetingButtons({ meeting, compact = false }) {
  const [copied, setCopied] = useState(false);
  const link = buildJoinLink(meeting.code);
  const shareText = buildShareText(meeting);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Join link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      toast.error("Could not copy, long-press to copy manually.");
    }
  };

  // mailto: spec — both subject and body go in querystring, RFC-3986 encoded.
  const mailHref = `mailto:?subject=${encodeURIComponent(`Invite · ${meeting.title}`)}&body=${encodeURIComponent(shareText)}`;
  // wa.me works on mobile (opens app) AND web.whatsapp.com on desktop.
  const waHref = `https://wa.me/?text=${encodeURIComponent(shareText)}`;

  if (compact) {
    return (
      <div className="inline-flex items-center gap-1" data-testid={`share-${meeting.meeting_id || meeting.code}`}>
        <button
          onClick={copyLink}
          title="Copy join link"
          data-testid={`share-copy-${meeting.meeting_id || meeting.code}`}
          className="p-2 text-navy/55 hover:text-saffron"
        >
          {copied ? <Check size={15} /> : <LinkIcon size={15} />}
        </button>
        <a
          href={mailHref}
          title="Share via email"
          data-testid={`share-email-${meeting.meeting_id || meeting.code}`}
          className="p-2 text-navy/55 hover:text-saffron"
        >
          <Mail size={15} />
        </a>
        <a
          href={waHref}
          target="_blank"
          rel="noopener noreferrer"
          title="Share on WhatsApp"
          data-testid={`share-whatsapp-${meeting.meeting_id || meeting.code}`}
          className="p-2 text-navy/55 hover:text-emerald-600"
        >
          <MessageCircle size={15} />
        </a>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-3 gap-2" data-testid={`share-${meeting.meeting_id || meeting.code}`}>
      <button
        onClick={copyLink}
        data-testid={`share-copy-${meeting.meeting_id || meeting.code}`}
        className="inline-flex items-center justify-center gap-2 px-3 py-2.5 border border-navy/15 text-navy hover:border-saffron hover:text-saffron rounded-md text-sm transition-colors"
      >
        {copied ? <Check size={14} /> : <LinkIcon size={14} />}
        <span>{copied ? "Copied" : "Copy link"}</span>
      </button>
      <a
        href={mailHref}
        data-testid={`share-email-${meeting.meeting_id || meeting.code}`}
        className="inline-flex items-center justify-center gap-2 px-3 py-2.5 border border-navy/15 text-navy hover:border-saffron hover:text-saffron rounded-md text-sm transition-colors"
      >
        <Mail size={14} /> Email
      </a>
      <a
        href={waHref}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`share-whatsapp-${meeting.meeting_id || meeting.code}`}
        className="inline-flex items-center justify-center gap-2 px-3 py-2.5 bg-emerald-600 text-white hover:bg-emerald-700 rounded-md text-sm transition-colors"
      >
        <MessageCircle size={14} /> WhatsApp
      </a>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// LOBBY — host (paid) creates new meetings; everyone enters a code to join.
// ──────────────────────────────────────────────────────────────────────────────
export function MeetingLobby() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [canHost, setCanHost] = useState(false);
  const [tier, setTier] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [myMeetings, setMyMeetings] = useState([]);
  const [code, setCode] = useState("");
  const [joining, setJoining] = useState(false);

  // Prefill the code from ?code= query param so shared links auto-fill it.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const c = params.get("code");
    if (c) {
      setCode(c.trim().toUpperCase());
      // Light scroll cue so members see the prefilled join box on mobile
      setTimeout(() => {
        document.querySelector('[data-testid="meeting-join-form"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 200);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    api.get("/platform/meetings/can-host").then((r) => {
      setCanHost(!!r.data.can_host);
      setTier(r.data.membership_tier || (r.data.role === "admin" ? "admin" : "community"));
    }).catch(() => {});
    api.get("/platform/meetings/mine").then((r) => {
      setMyMeetings(r.data.meetings || []);
    }).catch(() => {});
  }, [user]);

  const join = async (e) => {
    e?.preventDefault?.();
    const c = code.trim().toUpperCase();
    if (!c) return;
    setJoining(true);
    try {
      const res = await api.post("/platform/meetings/join", { code: c });
      const m = res.data;
      // Stash so the room page knows the meeting metadata
      sessionStorage.setItem(`meeting:${m.room_name}`, JSON.stringify(m));
      navigate(`/platform/meetings/room/${encodeURIComponent(m.room_name)}`);
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || "Could not join.";
      toast.error(msg);
    } finally {
      setJoining(false);
    }
  };

  const meetingCreated = (m) => {
    // Add to the list, but DO NOT close the modal — the modal switches to its
    // success step where the host can share via Email / WhatsApp / link.
    setMyMeetings((prev) => [m, ...prev]);
    toast.success(`Meeting created, code ${m.code}`);
  };

  if (!user) {
    return (
      <div className="px-4 sm:px-8 lg:px-10 py-12 max-w-2xl mx-auto" data-testid="meeting-lobby-login">
        <div className="rounded-xl bg-white border border-navy/10 p-8 text-center shadow-sm">
          <Lock size={28} className="mx-auto text-saffron mb-3" />
          <h1 className="font-display text-3xl text-navy">Login to access meetings</h1>
          <p className="mt-2 text-navy/65">Sangha meetings are available to logged-in members.</p>
          <Link
            to="/login"
            data-testid="meeting-login-cta"
            className="inline-block mt-5 px-5 py-2.5 bg-navy text-pearl rounded-md text-sm hover:bg-navy-light"
          >
            Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-8 lg:px-10 py-8 max-w-5xl mx-auto" data-testid="meeting-lobby">
      <Link to="/platform" className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy mb-6">
        <ArrowLeft size={14} /> Back to dashboard
      </Link>
      <p className="text-[10px] uppercase tracking-[0.25em] text-saffron">Invite-only · Encrypted</p>
      <h1 className="font-display text-4xl text-navy">Sangha Meetings</h1>
      <p className="mt-2 text-navy/65 max-w-2xl">
        Free, end-to-end encrypted video calls for up to ~100 members, invite-only by code.
        Annual and Lifetime members can host; anyone with a valid code can join.
      </p>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* HOST tile, paid-only */}
        {canHost ? (
          <div className="rounded-xl bg-gradient-to-br from-[#0B1F4D] to-[#2D5BFF] text-pearl p-7 shadow-lg">
            <div className="flex items-center justify-between">
              <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center">
                <Video size={22} className="text-saffron" />
              </div>
              <span className="text-[10px] uppercase tracking-widest text-saffron/90 inline-flex items-center gap-1">
                <Crown size={11} /> {tier === "admin" ? "Admin" : tier} member
              </span>
            </div>
            <h2 className="mt-4 font-display text-2xl text-pearl">Host a new meeting</h2>
            <p className="mt-2 text-pearl/75 text-sm leading-relaxed">
              Create an invite-only Sangha room. Share the 8-character code with members
              and they join in a click, no app, no signup needed.
            </p>
            <button
              onClick={() => setShowCreate(true)}
              data-testid="meeting-create-btn"
              className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 bg-saffron text-navy rounded-md text-sm font-medium hover:bg-saffron/90"
            >
              <Plus size={16} /> Create meeting
            </button>
          </div>
        ) : (
          <div className="rounded-xl bg-white border border-saffron/30 p-7 shadow-sm" data-testid="meeting-upgrade-tile">
            <div className="w-12 h-12 rounded-lg bg-saffron/10 flex items-center justify-center mb-4">
              <ShieldAlert size={22} className="text-saffron" />
            </div>
            <h2 className="font-display text-2xl text-navy">Hosting is for paid members</h2>
            <p className="mt-2 text-navy/70 text-sm">
              Annual (£24/year) and Lifetime members can host invite-only Sangha meetings,
              schedule recurring sits, and lead study circles. Free Community members can
              still join any meeting they're invited to.
            </p>
            <Link
              to="/membership"
              data-testid="meeting-upgrade-cta"
              className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 bg-navy text-pearl rounded-md text-sm hover:bg-navy-light"
            >
              <Crown size={14} /> Upgrade to host
            </Link>
          </div>
        )}

        {/* JOIN tile, everyone */}
        <form
          onSubmit={join}
          className="rounded-xl bg-white border border-navy/10 p-7 shadow-sm"
          data-testid="meeting-join-form"
        >
          <div className="w-12 h-12 rounded-lg bg-navy/5 flex items-center justify-center mb-4">
            <Hash size={22} className="text-saffron" />
          </div>
          <h2 className="font-display text-2xl text-navy">Join with a code</h2>
          <p className="mt-2 text-navy/70 text-sm">
            Paste the 8-character code your host shared (e.g. <code className="bg-pearl px-1.5 py-0.5 rounded font-mono text-xs">BHIM-7A3K</code>).
          </p>
          <div className="mt-5 flex gap-2">
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="BHIM-7A3K"
              data-testid="meeting-join-code"
              className="form-input flex-1 font-mono tracking-widest text-center uppercase"
              maxLength={20}
              autoCapitalize="characters"
            />
            <button
              type="submit"
              disabled={!code.trim() || joining}
              data-testid="meeting-join-submit"
              className="px-5 py-2 bg-navy text-pearl rounded-md text-sm hover:bg-navy-light disabled:opacity-40"
            >
              {joining ? "Joining…" : "Join"}
            </button>
          </div>
        </form>
      </div>

      {/* My meetings list */}
      {canHost && myMeetings.length > 0 && (
        <section className="mt-12" data-testid="meeting-my-list">
          <h2 className="font-display text-xl text-navy mb-3">Your meetings</h2>
          <div className="bg-white border border-navy/10 rounded-xl divide-y divide-navy/5 overflow-hidden">
            {myMeetings.map((m) => (
              <MyMeetingRow
                key={m.meeting_id}
                meeting={m}
                onDelete={(id) => setMyMeetings((prev) => prev.filter((x) => x.meeting_id !== id))}
              />
            ))}
          </div>
        </section>
      )}

      <div className="mt-12 rounded-xl bg-pearl border border-navy/10 p-6 grid grid-cols-1 sm:grid-cols-3 gap-6 text-sm">
        <div className="flex gap-3">
          <Users size={18} className="text-saffron flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-medium text-navy">Up to ~100 participants</div>
            <div className="text-navy/60 text-xs">Free Jitsi tier · plenty for any chapter meeting</div>
          </div>
        </div>
        <div className="flex gap-3">
          <Lock size={18} className="text-saffron flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-medium text-navy">Invite-only by code</div>
            <div className="text-navy/60 text-xs">Room URLs are unguessable · code is the key</div>
          </div>
        </div>
        <div className="flex gap-3">
          <Sparkles size={18} className="text-saffron flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-medium text-navy">No app needed</div>
            <div className="text-navy/60 text-xs">Works on any modern browser, mobile or desktop</div>
          </div>
        </div>
      </div>

      {showCreate && (
        <CreateMeetingModal onClose={() => setShowCreate(false)} onCreated={meetingCreated} />
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
function MyMeetingRow({ meeting, onDelete }) {
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(meeting.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) { /* ignore */ }
  };
  const enter = () => {
    sessionStorage.setItem(`meeting:${meeting.room_name}`, JSON.stringify(meeting));
    navigate(`/platform/meetings/room/${encodeURIComponent(meeting.room_name)}`);
  };
  const remove = async () => {
    if (!window.confirm(`Delete meeting "${meeting.title}"? This invalidates the invite code immediately.`)) return;
    try {
      await api.delete(`/platform/meetings/${meeting.meeting_id}`);
      toast.success("Meeting deleted");
      onDelete?.(meeting.meeting_id);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not delete meeting.");
    }
  };
  return (
    <div className="flex items-center gap-4 p-4 hover:bg-pearl/50" data-testid={`my-meeting-${meeting.meeting_id}`}>
      <div className="flex-1 min-w-0">
        <div className="font-display text-base text-navy truncate">{meeting.title}</div>
        {meeting.description && (
          <div className="text-xs text-navy/60 truncate mt-0.5">{meeting.description}</div>
        )}
        {(meeting.start_at || meeting.location) && (
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-navy/55">
            {meeting.start_at && (
              <span className="inline-flex items-center gap-1">
                <Clock size={10} />
                {(() => {
                  try {
                    return new Date(meeting.start_at).toLocaleString("en-GB", {
                      weekday: "short", day: "numeric", month: "short",
                      hour: "2-digit", minute: "2-digit",
                    });
                  } catch { return meeting.start_at; }
                })()}
                {meeting.duration_min ? ` · ${meeting.duration_min} min` : ""}
              </span>
            )}
            {meeting.location && (
              <span className="inline-flex items-center gap-1 truncate max-w-[16rem]">
                <MapPin size={10} /> {meeting.location}
              </span>
            )}
            {!meeting.location && meeting.mode === "online" && (
              <span className="inline-flex items-center gap-1 text-emerald-700">
                <Video size={10} /> Online
              </span>
            )}
          </div>
        )}
      </div>
      <div className="hidden sm:block text-right">
        <div className="text-[10px] uppercase tracking-widest text-navy/50">Code</div>
        <div className="font-mono text-sm text-navy tracking-widest">{meeting.code}</div>
      </div>
      <button
        onClick={copy}
        data-testid={`my-meeting-copy-${meeting.meeting_id}`}
        className="p-2 text-navy/55 hover:text-saffron"
        title="Copy code"
      >
        {copied ? <Check size={15} /> : <Copy size={15} />}
      </button>
      <ShareMeetingButtons meeting={meeting} compact />
      <button
        onClick={remove}
        data-testid={`my-meeting-delete-${meeting.meeting_id}`}
        className="p-2 text-navy/45 hover:text-red-700"
        title="Delete meeting"
      >
        <Trash2 size={15} />
      </button>
      <button
        onClick={enter}
        data-testid={`my-meeting-enter-${meeting.meeting_id}`}
        className="px-3.5 py-1.5 bg-navy text-pearl rounded-md text-xs hover:bg-navy-light inline-flex items-center gap-1.5"
      >
        Enter <ExternalLink size={11} />
      </button>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
function CreateMeetingModal({ onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [startAt, setStartAt] = useState("");
  const [durationMin, setDurationMin] = useState("60");
  const [mode, setMode] = useState("online");
  const [location, setLocation] = useState("");
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || busy) return;
    setBusy(true);
    try {
      // Convert local datetime input → ISO with TZ offset (or omit if blank)
      const startIso = startAt ? new Date(startAt).toISOString() : null;
      const res = await api.post("/platform/meetings", {
        title: title.trim(),
        description: description.trim() || null,
        start_at: startIso,
        duration_min: startAt && durationMin ? Number(durationMin) : null,
        mode,
        location: mode === "online" ? null : (location.trim() || null),
      });
      setCreated(res.data);
      onCreated(res.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not create meeting.");
    } finally {
      setBusy(false);
    }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(created.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) { /* ignore */ }
  };

  const enter = () => {
    sessionStorage.setItem(`meeting:${created.room_name}`, JSON.stringify(created));
    navigate(`/platform/meetings/room/${encodeURIComponent(created.room_name)}`);
  };

  return (
    <div className="fixed inset-0 z-50 bg-navy/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="create-meeting-modal">
      <div className="bg-pearl rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-navy/10">
          <h2 className="font-display text-lg text-navy">{created ? "Meeting created" : "Create a meeting"}</h2>
          <button onClick={onClose} className="p-1 text-navy/50 hover:text-navy" data-testid="create-meeting-close">
            <X size={18} />
          </button>
        </div>

        {!created ? (
          <form onSubmit={submit} className="p-5 space-y-4">
            <label className="block">
              <span className="block text-xs uppercase tracking-widest text-navy/60 mb-1">Title *</span>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                maxLength={120}
                placeholder="Sunday Dhamma study circle"
                className="form-input w-full"
                data-testid="create-meeting-title"
                autoFocus
              />
            </label>
            <label className="block">
              <span className="block text-xs uppercase tracking-widest text-navy/60 mb-1">Description (optional)</span>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                maxLength={600}
                placeholder="What will you cover? Any reading to do beforehand?"
                className="form-input w-full"
                data-testid="create-meeting-description"
              />
            </label>

            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="block text-xs uppercase tracking-widest text-navy/60 mb-1">Start (optional)</span>
                <input
                  type="datetime-local"
                  value={startAt}
                  onChange={(e) => setStartAt(e.target.value)}
                  className="form-input w-full"
                  data-testid="create-meeting-start"
                />
              </label>
              <label className="block">
                <span className="block text-xs uppercase tracking-widest text-navy/60 mb-1">Duration</span>
                <select
                  value={durationMin}
                  onChange={(e) => setDurationMin(e.target.value)}
                  disabled={!startAt}
                  className="form-input w-full disabled:opacity-50"
                  data-testid="create-meeting-duration"
                >
                  <option value="30">30 min</option>
                  <option value="45">45 min</option>
                  <option value="60">1 hour</option>
                  <option value="90">1.5 hours</option>
                  <option value="120">2 hours</option>
                </select>
              </label>
            </div>

            <div>
              <span className="block text-xs uppercase tracking-widest text-navy/60 mb-1">Format</span>
              <div className="flex gap-2" data-testid="create-meeting-mode">
                {[
                  { id: "online", label: "Online" },
                  { id: "hybrid", label: "Hybrid" },
                  { id: "in-person", label: "In-person" },
                ].map((m) => (
                  <button
                    type="button"
                    key={m.id}
                    onClick={() => setMode(m.id)}
                    data-testid={`create-meeting-mode-${m.id}`}
                    className={`flex-1 px-3 py-2 rounded-md text-xs border transition-colors ${
                      mode === m.id
                        ? "bg-navy text-pearl border-navy"
                        : "bg-white text-navy/70 border-navy/15 hover:border-saffron"
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            {mode !== "online" && (
              <label className="block">
                <span className="block text-xs uppercase tracking-widest text-navy/60 mb-1">
                  Location {mode === "in-person" ? "*" : "(if hybrid)"}
                </span>
                <input
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="Stockport Buddha Vihara, 11 Wellington Rd"
                  className="form-input w-full"
                  data-testid="create-meeting-location"
                  required={mode === "in-person"}
                  maxLength={200}
                />
              </label>
            )}

            {startAt && (
              <p className="text-[11px] text-saffron bg-saffron/10 border border-saffron/30 px-3 py-2 rounded-md flex items-start gap-2" data-testid="create-meeting-cal-hint">
                <CalIcon size={12} className="mt-0.5 flex-shrink-0" />
                <span>This meeting will appear on the ABGN-UK Calendar and in members' .ics feeds (Apple / Google / Outlook).</span>
              </p>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={onClose} className="px-4 py-2 text-navy/60 hover:text-navy text-sm">Cancel</button>
              <button
                type="submit"
                disabled={!title.trim() || busy}
                data-testid="create-meeting-submit"
                className="px-5 py-2 bg-navy text-pearl rounded-md text-sm hover:bg-navy-light disabled:opacity-40"
              >
                {busy ? "Creating…" : "Create"}
              </button>
            </div>
          </form>
        ) : (
          <div className="p-5 space-y-5" data-testid="create-meeting-success">
            <div className="text-center">
              <p className="text-xs uppercase tracking-[0.25em] text-saffron">Share this code with members</p>
              <div className="mt-2 inline-block bg-navy text-pearl px-6 py-3 rounded-lg font-mono text-2xl tracking-[0.3em]">
                {created.code}
              </div>
              <button
                onClick={copyCode}
                data-testid="create-meeting-copy-code"
                className="ml-2 inline-flex items-center gap-1.5 text-xs text-navy/60 hover:text-saffron"
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
                {copied ? "Copied!" : "Copy code"}
              </button>
            </div>
            <div className="rounded-md bg-pearl border border-navy/10 px-4 py-3 text-sm">
              <div className="font-medium text-navy">{created.title}</div>
              {created.description && <div className="text-navy/60 mt-1">{created.description}</div>}
              {(created.start_at || created.location) && (
                <div className="mt-2 pt-2 border-t border-navy/10 space-y-1 text-xs" data-testid="created-meta">
                  {created.start_at && (
                    <div className="flex items-center gap-2 text-navy/75">
                      <Clock size={12} className="text-saffron" />
                      {new Date(created.start_at).toLocaleString("en-GB", {
                        weekday: "long", day: "numeric", month: "long",
                        hour: "2-digit", minute: "2-digit",
                      })}
                      {created.duration_min ? ` · ${created.duration_min} min` : ""}
                    </div>
                  )}
                  {created.location && (
                    <div className="flex items-start gap-2 text-navy/75">
                      <MapPin size={12} className="text-saffron mt-0.5 flex-shrink-0" />
                      <span>{created.location}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
            <p className="text-xs text-navy/55 text-center italic">
              Members go to /platform/meetings → "Join with a code" → paste this code.
              Or share the direct link/email/WhatsApp below.
            </p>
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-saffron mb-2 text-center">
                Share invite
              </p>
              <ShareMeetingButtons meeting={created} />
            </div>
            <div className="flex gap-2">
              <button onClick={onClose} className="flex-1 px-4 py-2 border border-navy/15 text-navy text-sm rounded-md hover:bg-navy/5">Done</button>
              <button
                onClick={enter}
                data-testid="create-meeting-enter"
                className="flex-1 px-4 py-2 bg-navy text-pearl rounded-md text-sm hover:bg-navy-light inline-flex items-center justify-center gap-2"
              >
                <Video size={14} /> Enter now
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// MEETING ROOM — embedded Jitsi for an unguessable room name.
// Reachable only through code-validated navigation OR by host's own list.
// The actual Jitsi iframe lives in <ActiveMeetingProvider> at the layout
// level so it survives route changes; here we just announce the meeting and
// anchor the iframe over our placeholder div.
// ──────────────────────────────────────────────────────────────────────────────

export function MeetingRoom() {
  const { roomName } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const meetingCtx = useActiveMeeting();
  const { active, joined, audioShared, launch, hangup, toggleShareAudio, setAnchor, apiRef } = meetingCtx;

  const anchorRef = useRef(null);

  // Hydrate meeting meta from sessionStorage (set by lobby) — synchronously
  // on first render so the launch effect sees the code immediately.
  const [meta, setMeta] = useState(() => {
    try {
      const cached = sessionStorage.getItem(`meeting:${roomName}`);
      if (cached) return JSON.parse(cached);
    } catch (e) { /* ignore */ }
    return null;
  });
  const [permission, setPermission] = useState({ mic: "unknown", cam: "unknown" });
  const [broadcast, setBroadcast] = useState(() => {
    try { return localStorage.getItem("bhimsangha:broadcast") === "1"; } catch { return false; }
  });
  const [themeId, setThemeId] = useState(() => {
    try { return localStorage.getItem("bhimsangha:broadcast:theme") || "auto"; } catch { return "auto"; }
  });
  const [showThemePicker, setShowThemePicker] = useState(false);

  const resolvedTheme = themeId === "auto" ? detectTheme(meta?.title) : themeId;

  const toggleBroadcast = () => {
    setBroadcast((b) => {
      const next = !b;
      try { localStorage.setItem("bhimsangha:broadcast", next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };

  const pickTheme = (id) => {
    setThemeId(id);
    try { localStorage.setItem("bhimsangha:broadcast:theme", id); } catch { /* ignore */ }
    setShowThemePicker(false);
  };

  // Pre-flight permission check — non-blocking, just informs the user.
  useEffect(() => {
    if (!navigator?.permissions?.query) return;
    (async () => {
      try {
        const mic = await navigator.permissions.query({ name: "microphone" });
        const cam = await navigator.permissions.query({ name: "camera" });
        setPermission({ mic: mic.state, cam: cam.state });
        mic.onchange = () => setPermission((p) => ({ ...p, mic: mic.state }));
        cam.onchange = () => setPermission((p) => ({ ...p, cam: cam.state }));
      } catch (e) { /* permissions API not supported (Safari iOS), silent */ }
    })();
  }, []);

  // If sessionStorage was empty (direct URL refresh / new tab), fetch meta
  // by room_name so we can still issue a JaaS-authenticated JWT and keep
  // moderator privileges. Without this, refresh would drop us into the JaaS
  // "asking to join meeting / login failed" lobby.
  useEffect(() => {
    if (meta || !roomName) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/platform/meetings/by-room/${encodeURIComponent(roomName)}`);
        if (!cancelled && r.data?.code) {
          setMeta(r.data);
          try {
            sessionStorage.setItem(`meeting:${roomName}`, JSON.stringify(r.data));
          } catch (e) { /* ignore */ }
        }
      } catch (e) { /* meeting not in our DB or no auth, falls back to public Jitsi */ }
    })();
    return () => { cancelled = true; };
  }, [roomName, meta]);

  // Launch (or re-anchor) the meeting whenever this route is visited
  useEffect(() => {
    if (!roomName) return;
    launch({
      roomName,
      code: meta?.code,
      title: meta?.title || "Sangha meeting",
      displayName: user?.name,
      email: user?.email,
    });
  }, [roomName, meta?.code, meta?.title]);

  // Tell the provider where to render the iframe; clear on unmount → PiP.
  useEffect(() => {
    setAnchor(anchorRef.current);
    return () => setAnchor(null);
  }, [setAnchor]);

  const isThisRoom = active?.roomName === roomName;

  return (
    <div className="flex flex-col h-[calc(100vh-3.5rem)] lg:h-screen bg-[#0B1F4D]" data-testid="meeting-room">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-6 py-3 bg-navy text-pearl border-b border-white/10">
        <div className="flex items-center gap-3 min-w-0">
          <Link to="/platform/meetings" className="text-pearl/60 hover:text-pearl">
            <ArrowLeft size={18} />
          </Link>
          <div className="min-w-0">
            <div className="text-sm font-medium truncate">
              {meta?.title || "Sangha meeting"}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-pearl/55">
              {isThisRoom && joined ? "Connected" : "Connecting…"} ·
              {meta?.code ? <> Code <span className="font-mono text-saffron">{meta.code}</span> · </> : " "}
              Powered by Jitsi
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 relative flex-wrap">
          <MeetingNotesInline meetingTitle={meta?.title} meetingCode={meta?.code} />
          <VirtualBackgroundPicker apiRef={apiRef} />
          {/* Share Audio, host can broadcast their tab/system audio (e.g.
              ambient music) to every participant. Jitsi's built-in flow. */}
          <button
            onClick={() => {
              toggleShareAudio();
              if (!audioShared) toast.info("Pick a browser tab with audio to share with everyone");
            }}
            data-testid="meeting-share-audio"
            title={audioShared ? "Stop sharing audio" : "Share music/audio with all participants"}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
              audioShared
                ? "bg-saffron/20 border-saffron text-saffron"
                : "bg-white/5 border-white/15 text-pearl/80 hover:border-saffron hover:text-saffron"
            }`}
          >
            {audioShared ? <Volume2 size={13} /> : <VolumeX size={13} />}
            {audioShared ? "Sharing music" : "Share music"}
          </button>
          {broadcast && (
            <div className="relative">
              <button
                onClick={() => setShowThemePicker((s) => !s)}
                data-testid="broadcast-theme-button"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-white/5 border border-white/15 text-pearl/80 hover:border-saffron hover:text-saffron"
                title="Choose broadcast theme"
              >
                <Sparkles size={13} />
                {themeId === "auto" ? `Auto · ${THEMES[resolvedTheme]?.label}` : THEMES[resolvedTheme]?.label}
              </button>
              {showThemePicker && (
                <div
                  data-testid="broadcast-theme-picker"
                  className="absolute right-0 top-full mt-2 w-72 bg-[#0B1F4D] border border-white/15 rounded-lg shadow-2xl p-2 z-50"
                >
                  <button
                    onClick={() => pickTheme("auto")}
                    data-testid="broadcast-theme-auto"
                    className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                      themeId === "auto" ? "bg-saffron/20 text-saffron" : "text-pearl/80 hover:bg-white/10"
                    }`}
                  >
                    <div className="font-medium inline-flex items-center gap-2"><Sparkles size={14} /> Auto-detect</div>
                    <div className="text-[10px] text-pearl/55 mt-0.5">Picks a theme from the meeting title</div>
                  </button>
                  <div className="h-px bg-white/10 my-1" />
                  {Object.values(THEMES).map((t) => {
                    const Icon = t.icon;
                    const active2 = themeId === t.id;
                    return (
                      <button
                        key={t.id}
                        onClick={() => pickTheme(t.id)}
                        data-testid={`broadcast-theme-${t.id}`}
                        className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                          active2 ? "bg-saffron/20 text-saffron" : "text-pearl/80 hover:bg-white/10"
                        }`}
                      >
                        <div className="font-medium inline-flex items-center gap-2"><Icon size={14} /> {t.label}</div>
                        <div className="text-[10px] text-pearl/55 mt-0.5">{t.desc}</div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          <button
            onClick={toggleBroadcast}
            data-testid="broadcast-toggle"
            title={broadcast ? "Turn off broadcast overlay" : "Turn on broadcast (TV/podcast) overlay"}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
              broadcast
                ? "bg-red-600/90 border-red-500 text-white hover:bg-red-600"
                : "bg-white/5 border-white/15 text-pearl/80 hover:border-saffron hover:text-saffron"
            }`}
          >
            <Radio size={13} />
            {broadcast ? "Broadcast: ON" : "Broadcast Mode"}
          </button>
          {meta?.code && <CopyCodeButton code={meta.code} />}
        </div>
      </div>

      {/* Permission helper banner, only shown if either stream is denied */}
      {(permission.mic === "denied" || permission.cam === "denied") && (
        <div
          data-testid="meeting-permission-warning"
          className="px-4 py-2.5 bg-amber-500/15 border-b border-amber-500/30 text-amber-100 text-xs flex items-start gap-2"
        >
          <ShieldAlert size={14} className="mt-0.5 flex-shrink-0" />
          <div>
            <strong>Microphone or camera blocked.</strong>{" "}
            Tap the lock icon in your browser's address bar →
            site settings → allow Microphone & Camera, then refresh.
            {permission.mic === "denied" && <> Mic blocked.</>}
            {permission.cam === "denied" && <> Camera blocked.</>}
          </div>
        </div>
      )}

      <div className="flex-1 relative">
        <BroadcastFrame
          on={broadcast}
          themeId={resolvedTheme}
          title={meta?.title || "ABGN-UK Sangha Meeting"}
          subtitle={meta?.code ? `Code · ${meta.code}` : "ABGN-UK · Global Sangha"}
          hostName={user?.name}
        >
          {/* The persistent Jitsi iframe is moved into this anchor by
              <ActiveMeetingProvider>. It survives navigation. */}
          <div ref={anchorRef} className="w-full h-full" data-testid="jitsi-anchor" />
        </BroadcastFrame>
      </div>
    </div>
  );
}

function CopyCodeButton({ code }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) { /* ignore */ }
  };
  return (
    <button
      onClick={copy}
      data-testid="meeting-copy-code"
      className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 border border-white/15 rounded-md text-xs text-pearl hover:bg-white/15"
    >
      {copied ? <Check size={14} /> : <Copy size={14} />}
      {copied ? "Copied!" : `Copy code ${code}`}
    </button>
  );
}

export default MeetingLobby;
